import React, { useEffect, useState } from 'react';
import ReportLayout from '../../components/reports/ReportLayout';
import ReportNote from '../../components/reports/ReportNote';
import FinancialReportSection, {
  FinancialReportRow
} from '../../components/reports/FinancialReportSection';
import { useClient } from '../../context/ClientContext';
import { cashFlowReportService } from '../../services/cashFlowReportService';
import {
  downloadCashFlowCsv,
  downloadCashFlowPdf
} from '../../utils/cashFlowExport';
import { formatCurrency, formatDate } from '../../utils/formatters';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiShield,
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiLayers,
  FiRefreshCw,
  FiDownload,
  FiCalendar,
  FiChevronDown
} = FiIcons;

const activitySections = [
  ['operating', 'Operating Activities'],
  ['investing', 'Investing Activities'],
  ['financing', 'Financing Activities']
];

const formatAmount = (value) => formatCurrency(Number(value || 0));

const ActivityRows = ({ section, title, viewMode }) => (
  <div className="mb-4 border-b border-slate-100 pb-4 last:border-0">
    <div className="mb-2 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-900">
        {title}
      </h3>
      <span className={`text-sm font-semibold ${
        section.total >= 0 ? 'text-emerald-600' : 'text-rose-600'
      }`}>
        {formatAmount(section.total)}
      </span>
    </div>

    {Object.entries(section.groups || {}).map(([groupName, group]) => (
      <div key={groupName} className="border-b border-slate-100 last:border-0">
        <div className="flex items-center justify-between px-3 py-2 text-sm">
          <span className="font-medium text-slate-700">{groupName}</span>
          <span className={`font-medium ${
            group.total < 0 ? 'text-rose-600' : 'text-slate-700'
          }`}>
            {formatAmount(group.total)}
          </span>
        </div>

        {viewMode === 'details' && (group.accounts || []).map((account) => (
          <div
            key={account.id}
            className="flex items-center justify-between px-7 py-1.5 text-sm"
          >
            <span className="text-slate-500">{account.name}</span>
            <span className={account.amount < 0 ? 'text-rose-600' : 'text-slate-600'}>
              {formatAmount(account.amount)}
            </span>
          </div>
        ))}

        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/60 px-3 py-2 text-sm font-medium">
          <span>Total {groupName}</span>
          <span>{formatAmount(group.total)}</span>
        </div>
      </div>
    ))}

    <div className="flex items-center justify-between px-3 pt-3 text-sm font-semibold text-slate-900">
      <span>Net Cash from {title}</span>
      <span>{formatAmount(section.total)}</span>
    </div>
  </div>
);

const SummaryOverview = ({ report }) => (
  <div className="mt-7 border-t border-slate-900 pt-5">
    <div className="mb-5 flex items-center justify-between">
      <h3 className="text-lg font-semibold uppercase tracking-wider text-slate-900">
        Net Increase (Decrease) in Cash
      </h3>
      <span className={`text-2xl font-semibold ${
        report.netChange >= 0 ? 'text-emerald-600' : 'text-rose-600'
      }`}>
        {formatCurrency(report.netChange)}
      </span>
    </div>

    <div className="space-y-3 rounded-2xl bg-slate-900 p-6 shadow-lg">
      <div className="flex items-center justify-between text-sm font-medium text-slate-400">
        <span className="text-[10px] uppercase tracking-widest">
          Cash at beginning of period
        </span>
        <span className="font-semibold text-white">
          {formatCurrency(report.beginningCash)}
        </span>
      </div>

      <div className="flex items-center justify-between text-sm font-medium text-slate-400">
        <span className="text-[10px] uppercase tracking-widest">
          Gross cash inflow
        </span>
        <span className="font-semibold text-emerald-300">
          {formatCurrency(report.grossInflow)}
        </span>
      </div>

      <div className="flex items-center justify-between text-sm font-medium text-slate-400">
        <span className="text-[10px] uppercase tracking-widest">
          Gross cash outflow
        </span>
        <span className="font-semibold text-rose-300">
          {formatCurrency(-report.grossOutflow)}
        </span>
      </div>

      <div className="flex items-center justify-between border-t border-white/10 pt-4">
        <div>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-300">
            Final Balance
          </span>
          <span className="text-xl font-semibold uppercase tracking-tight text-white">
            Cash at end of period
          </span>
        </div>
        <span className="text-4xl font-semibold text-white">
          {formatCurrency(report.endingCash)}
        </span>
      </div>
    </div>
  </div>
);

const CashFlowGroupRows = ({ groups }) => (
  <>
    {Object.entries(groups || {}).map(([groupName, group]) => (
      <React.Fragment key={groupName}>
        <FinancialReportRow label={groupName} value={group.total} />
        {(group.accounts || []).map((account) => (
          <FinancialReportRow
            key={account.id}
            label={account.name}
            value={account.amount}
            muted
          />
        ))}
        <FinancialReportRow
          label={`Total ${groupName}`}
          value={group.total}
          total
        />
      </React.Fragment>
    ))}
  </>
);

export default function CashFlowReport() {
  const { activeClient } = useClient();
  const [viewMode, setViewMode] = useState('summary');
  const [exportOpen, setExportOpen] = useState(false);
  const [params, setParams] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1)
      .toISOString()
      .split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReport = async () => {
    if (!activeClient?.id) {
      setLoading(false);
      setReport(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await cashFlowReportService.generate(
        params.startDate,
        params.endDate,
        activeClient.id
      );
      setReport(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to generate cash flow statement');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [activeClient?.id, params.startDate, params.endDate]);

  const exportReport = (type) => {
    if (!report) return;

    const options = {
      report,
      startDate: params.startDate,
      endDate: params.endDate,
      viewMode
    };

    if (type === 'csv') {
      downloadCashFlowCsv(options);
    }

    if (type === 'pdf') {
      downloadCashFlowPdf(options);
    }

    setExportOpen(false);
  };

  const isValid = report && Math.abs(
    report.beginningCash + report.netChange - report.endingCash
  ) < 0.01;

  void ActivityRows;
  void SummaryOverview;
  void ReportNote;
  void SafeIcon;
  void FiShield;
  void FiAlertTriangle;
  void FiCheckCircle;
  void FiInfo;
  void FiLayers;
  void FiRefreshCw;
  void FiDownload;
  void FiCalendar;
  void FiChevronDown;
  void formatAmount;
  void formatDate;
  void isValid;

  return (
    <ReportLayout
      title="Cash Inflow & Outflow"
      description="Statement of Cash Flows."
      viewMode={viewMode}
      setViewMode={setViewMode}
      startDate={params.startDate}
      endDate={params.endDate}
      onStartDateChange={(value) => setParams((current) => ({
        ...current,
        startDate: value
      }))}
      onEndDateChange={(value) => setParams((current) => ({
        ...current,
        endDate: value
      }))}
      onRefresh={fetchReport}
      loading={loading}
      onExport={exportReport}
      exportOpen={exportOpen}
      setExportOpen={setExportOpen}
    >
      {!activeClient && (
        <p className="py-10 text-center text-sm text-slate-500">
          Select a business to view cash flow.
        </p>
      )}

      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <span>{error}</span>
          <button type="button" onClick={fetchReport} className="font-medium underline">
            Try again
          </button>
        </div>
      )}

      {report && (
        <div>
          {activitySections.map(([key, title]) => (
            <FinancialReportSection
              key={key}
              title={title}
              totalLabel={`Net Cash from ${title}`}
              total={report[key].total}
              emptyLabel=""
              rows={
                viewMode === 'details' && (
                  <CashFlowGroupRows groups={report[key].groups} />
                )
              }
            />
          ))}

          <FinancialReportRow
            label="Net Cash Change"
            value={report.netChange}
            total
            grand
          />
        </div>
      )}

      {!loading && activeClient && !report && !error && (
        <p className="py-10 text-center text-sm text-slate-500">
          No report data is available.
        </p>
      )}
    </ReportLayout>
  );
}